(() => {
  const rootUrl = () =>
    (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || "/";

  const money = (cents, currency) => {
    try {
      return new Intl.NumberFormat(document.documentElement.lang || "de-DE", {
        style: "currency",
        currency: currency || "EUR",
      }).format(cents / 100);
    } catch (_error) {
      return `${(cents / 100).toFixed(2).replace(".", ",")} €`;
    }
  };

  const announce = (message, isError = false) => {
    let toast = document.querySelector("[data-pw-toast]");
    if (!toast) {
      toast = document.createElement("div");
      toast.className = "pw-toast";
      toast.setAttribute("data-pw-toast", "");
      toast.setAttribute("role", "status");
      toast.setAttribute("aria-live", "polite");
      document.body.appendChild(toast);
    }

    toast.textContent = message;
    toast.classList.toggle("is-error", isError);
    toast.classList.add("is-visible");
    window.clearTimeout(toast.pwTimer);
    toast.pwTimer = window.setTimeout(() => {
      toast.classList.remove("is-visible");
    }, 2800);
  };

  const refreshCartCount = async () => {
    const response = await fetch(`${rootUrl()}cart.js`, {
      headers: { Accept: "application/json" },
    });
    if (!response.ok) throw new Error("Cart could not be refreshed");
    const cart = await response.json();
    document.querySelectorAll("[data-pw-cart-count]").forEach((counter) => {
      counter.textContent = cart.item_count;
      counter.setAttribute(
        "aria-label",
        `${cart.item_count} Artikel im Warenkorb`,
      );
    });
    document.dispatchEvent(
      new CustomEvent("pw:cart-updated", { detail: { cart } }),
    );
    return cart;
  };

  const handleQuickAdd = async (event) => {
    const form = event.target.closest("[data-pw-quick-add]");
    if (!form) return;

    event.preventDefault();
    const button = form.querySelector('button[type="submit"]');
    const originalLabel = button ? button.textContent : "";

    if (button) {
      button.disabled = true;
      button.textContent = button.dataset.loadingLabel || "Wird eingetütet …";
    }

    try {
      const cartDrawer = document.querySelector("cart-drawer");
      const formData = new FormData(form);

      if (cartDrawer && typeof cartDrawer.getSectionsToRender === "function") {
        formData.append(
          "sections",
          cartDrawer
            .getSectionsToRender()
            .map((section) => section.id)
            .join(","),
        );
        formData.append("sections_url", window.location.pathname);
        if (typeof cartDrawer.setActiveElement === "function") {
          cartDrawer.setActiveElement(button || form);
        }
      }

      const response = await fetch(`${rootUrl()}cart/add.js`, {
        method: "POST",
        headers: {
          Accept: "application/json",
          "X-Requested-With": "XMLHttpRequest",
        },
        body: formData,
      });

      const payload = await response.json();
      if (!response.ok) {
        throw new Error(
          payload.description || payload.message || "Hinzufügen fehlgeschlagen",
        );
      }

      if (
        cartDrawer &&
        payload.sections &&
        typeof cartDrawer.renderContents === "function"
      ) {
        cartDrawer.renderContents(payload);
      }

      await refreshCartCount();
      announce(
        form.dataset.successMessage ||
          `${payload.product_title || "Dein Candy"} ist jetzt im Warenkorb.`,
      );
    } catch (error) {
      announce(
        error && error.message
          ? error.message
          : "Das hat gerade nicht geklappt. Bitte versuche es noch einmal.",
        true,
      );
    } finally {
      if (button) {
        button.disabled = false;
        button.textContent = originalLabel;
      }
    }
  };

  const activateMood = (button) => {
    const section = button.closest("[data-pw-moods]");
    if (!section) return;
    const target = button.dataset.pwMoodTarget;

    section.querySelectorAll("[data-pw-mood-target]").forEach((item) => {
      const active = item === button;
      item.classList.toggle("is-active", active);
      item.setAttribute("aria-selected", active ? "true" : "false");
      item.setAttribute("tabindex", active ? "0" : "-1");
    });

    section.querySelectorAll("[data-pw-mood-panel]").forEach((panel) => {
      const active = panel.dataset.pwMoodPanel === target;
      panel.hidden = !active;
      panel.classList.toggle("is-active", active);
    });
  };

  const handleClick = (event) => {
    const cartLink = event.target.closest("[data-pw-cart-open]");
    if (cartLink) {
      const cartDrawer = document.querySelector("cart-drawer");
      if (cartDrawer && typeof cartDrawer.open === "function") {
        event.preventDefault();
        cartDrawer.open(cartLink);
        return;
      }
    }

    const moodButton = event.target.closest("[data-pw-mood-target]");
    if (moodButton) {
      activateMood(moodButton);
      return;
    }

    const mobileMenuLink = event.target.closest(".pw-mobile-menu a");
    if (mobileMenuLink) {
      const menu = mobileMenuLink.closest("details");
      if (menu) menu.removeAttribute("open");
    }
  };

  const handleKeydown = (event) => {
    const current = event.target.closest("[data-pw-mood-target]");
    if (!current || !["ArrowLeft", "ArrowRight"].includes(event.key)) return;

    const section = current.closest("[data-pw-moods]");
    const buttons = Array.from(
      section.querySelectorAll("[data-pw-mood-target]"),
    );
    const index = buttons.indexOf(current);
    const direction = event.key === "ArrowRight" ? 1 : -1;
    const next = buttons[(index + direction + buttons.length) % buttons.length];
    event.preventDefault();
    next.focus();
    activateMood(next);
  };

  const handleVariantChange = (event) => {
    const select = event.target.closest("[data-pw-variant-select]");
    if (!select) return;
    const option = select.options[select.selectedIndex];
    const scope = select.closest("[data-pw-product-scope]");
    if (!scope || !option) return;

    const price = scope.querySelector("[data-pw-product-price]");
    if (price && option.dataset.price) {
      price.textContent = money(
        Number(option.dataset.price),
        option.dataset.currency,
      );
    }
  };

  const initSection = (scope = document) => {
    scope.querySelectorAll("[data-pw-moods]").forEach((section) => {
      if (section.querySelector("[data-pw-mood-target].is-active")) return;
      const first = section.querySelector("[data-pw-mood-target]");
      if (first) activateMood(first);
    });
  };

  document.addEventListener("submit", handleQuickAdd);
  document.addEventListener("click", handleClick);
  document.addEventListener("keydown", handleKeydown);
  document.addEventListener("change", handleVariantChange);
  document.addEventListener("DOMContentLoaded", () => initSection(document));
  document.addEventListener("shopify:section:load", (event) =>
    initSection(event.target),
  );
})();
